import React, {useEffect} from 'react';
import {useGetCountriesQuery} from '@redux/api/internalApiSlice';
import {useTranslation} from 'react-i18next';
import BaseModal from '@reusables/Modals/BaseModal';
import BaseInputsGrid from '@reusables/BaseInputsGrid';
import BaseInput from '@reusables/BaseInput';
import BaseDropdown from '@reusables/dropdowns/BaseDropdown';
import {getCountryFlag} from '@helpers/utils';
import BaseButton from '@reusables/BaseButton';

import {Controller, useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from 'zod';
import {useAppSelector} from '@redux/hooks';
import {useCreateDeliveryAddressMutation} from '@redux/features/companies/companiesApi';
import BasePhoneInput from '@reusables/BasePhoneInput';
import {toast} from "react-toastify";
import {Company} from "@/types/general";
import {isErrorWithMessage} from "@redux/api/query";

interface CreateDeliveryAddressModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreation: (address: Company.Components.DeliveryAddress) => void;
}

const formScheme = z.object({
    name: z.string().trim(),
    country: z.object({
        id: z.coerce.number(),
        name: z.string(),
        code: z.string(),
    }),
    street: z.string().trim(),
    street2: z.string().trim().optional(),
    zipcode: z.string().trim(),
    city: z.string().trim(),
    email: z.string().trim(),
    contact_person: z.string().trim(),
    phone: z.string(),
});

type FormTyping = z.infer<typeof formScheme>

export default function CreateDeliveryAddressModal({isOpen, onClose, onCreation}: CreateDeliveryAddressModalProps): JSX.Element {
    const {t} = useTranslation("", {keyPrefix: "purchaseOrder.general.dropdowns.deliveryAddress.creationModal"});

    const companyStorageData = useAppSelector(state => state.auth.user?.company);

    // ---> Countries selection <--- //
    const {data: countriesOptions, isLoading: countriesOptionsLoading} = useGetCountriesQuery();

    const [createDeliveryAddress, {isLoading: createDeliveryAddressLoading}] = useCreateDeliveryAddressMutation();

    const {control, handleSubmit, reset} = useForm<FormTyping>({
        resolver: zodResolver(formScheme),
    });

    const country = useWatch({
        control,
        name: 'country',
    });

    useEffect(() => {
        reset();
    }, [reset, isOpen]);

    const onSubmit = handleSubmit((data) => {
        if(companyStorageData) {
            createDeliveryAddress({
                name: data.name,
                country: data.country.id,
                street: data.street,
                street_2: data.street2,
                zipcode: data.zipcode?.toString(),
                city: data.city,
                email: data.email,
                phone: data.phone,
                contact_person: data.contact_person,
            }).unwrap().then(delivery => {
                onCreation(delivery);
            }).catch(e => {
                console.log(e);
                if(isErrorWithMessage(e)){
                    toast.error(e.message);
                }else{
                    toast.error(t('responses.error'));
                }
            })
        }
    });

  return (
    <BaseModal
        keepMounted={false}
        isOpen={isOpen}
        onClose={onClose}
        width={900}
        padding="56px"
        useCloseIcon
        isLoading={createDeliveryAddressLoading}
    >
        <form className='space-y-[32px]' onSubmit={onSubmit}>
            <div className='modal-title'>{t('heading')}</div>
            <BaseInputsGrid gap={{ x:24, y: 40 }} cols={2}>
                <Controller 
                    name={'name'}
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t('fields.name')}
                        />
                    )}
                />

                <Controller 
                    name={'country'}
                    control={control}
                    render={({field, fieldState}) => (
                        <BaseDropdown
                            {...field}
                            {...fieldState}
                            label={t("dropdowns.country.label")}
                            placeholder={t("dropdowns.country.placeholder")}
                            options={countriesOptions}
                            getter={{
                                label: (opt) => opt.name,
                                key: (opt) => opt.id,
                                renderOption: (opt, icon) => (
                                    <div>
                                        <img src={getCountryFlag(opt.code)} width={20} alt={opt.code} />
                                        <span className="ml-[8px] grow">{opt.name}</span>
                                        {icon}
                                    </div>
                                )
                            }}

                            autocomplete
                            isLoading={countriesOptionsLoading}
                        />
                    )}
                />

                <Controller 
                    name={'street'}
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t('fields.street')}
                        />
                    )}
                />

                <Controller 
                    name={'street2'}
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t('fields.street2')}
                        />
                    )}
                />

                <BaseInputsGrid gap={{ x:24, y: 40 }} cols={2}>
                    <Controller 
                        name={'zipcode'}
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t('fields.zipCode')}
                            />
                        )}
                    />

                    <Controller 
                        name={'city'}
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t('fields.city')}
                            />
                        )}
                    />
                </BaseInputsGrid>

                <Controller 
                    name={'email'}
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t('fields.email')}
                        />
                    )}
                />

                <Controller 
                    name={'contact_person'}
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t('fields.contactName')}
                        />
                    )}
                />

                <Controller 
                    name={'phone'}
                    control={control}
                    render={({field, fieldState}) => (
                        <BasePhoneInput
                            {...field}
                            error={fieldState.error}
                            label={t('fields.phone')}
                            value={field.value || ''}
                            onChange={field.onChange}
                            size='md'
                            variant='custom'
                            country={!field.value ? country?.code : ""}
                        />
                    )}
                />
            </BaseInputsGrid>

            <BaseButton
                buttonWidth='100%'
                text={`${t("buttons.save")}`}
                size='md'
                type='submit'
            />
        </form>
    </BaseModal>
  )
}
